import { useState } from 'react'
import { PlayCircle } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ChannelToggle, type Channel } from '../components/ChannelToggle'
import { VideoGuidePanel, type VideoGuide } from '../components/VideoGuidePanel'

const SOCIAL_STEPS = [
  {
    step: 1,
    title: 'Review the Row in Content Tracker',
    items: [
      <>Open the assigned row and review the Asset Overlay (read-only)</>,
      <>Available info: Campaign objective, KPI, Platform, Placement, FordShare link, validated copy, asset preview</>,
    ],
    note: 'You cannot edit creative fields. All content has been locked by the Creative Agency approval.',
  },
  {
    step: 2,
    title: 'Set Up the Ad in the Platform',
    items: [
      <>Download the asset from FordShare — it must match what is shown in the preview</>,
      <>Copy-paste the primary text, headline, and description from the Asset Overlay</>,
      <>Set up the ad in the relevant platform (Meta, TikTok, LinkedIn, etc.)</>,
      <>Validate the ad inside the platform: confirm format, aspect ratio, no truncated text</>,
    ],
    note: null,
  },
  {
    step: 3,
    title: 'Add the Preview Link',
    items: [
      <>Generate a Preview Link inside the advertising platform</>,
      <>Paste it into the <strong className="text-slate-800">Preview Link</strong> field in Content Tracker</>,
      <>You can also add multiple Preview Links if needed</>,
      <>This automatically notifies the Client/Approver and moves the row to <strong className="text-slate-800">Waiting for Preview Approval</strong></>,
    ],
    note: null,
  },
  {
    step: 4,
    title: 'Respond to Client Feedback (if any)',
    items: [
      <>If the client leaves feedback, you will receive a Teams notification</>,
      <>Review the feedback in the Preview Comments section of the Status Overlay</>,
      <>Update the platform setup and generate a new Preview Link if required</>,
      <>You can also add multiple Preview Links if needed</>,
    ],
    note: null,
  },
  {
    step: 5,
    title: 'Publish the Campaign',
    items: [
      <>Once the client has approved, publish the campaign in the advertising platform</>,
      <>Return to Content Tracker and set <strong className="text-slate-800">Live Column = YES</strong></>,
      <>This generates the Live Date and sends a Teams notification: "Campaign is now live"</>,
    ],
    note: null,
  },
]

const SEARCH_STEPS = [
  {
    step: 1,
    title: 'Review the Row in Content Tracker',
    items: [
      <>Open the assigned row and review the Asset Overlay (read-only)</>,
      <>Available info: Campaign objective, KPI, Platform, Format, FordShare link, validated copy</>,
    ],
    note: 'You cannot edit creative fields. All content has been locked by the Creative Agency\'s approval.',
  },
  {
    step: 2,
    title: 'Set Up the Ad in the Platform',
    items: [
      <>Download the asset from FordShare — it must match what is shown in the preview</>,
      <>Copy-paste the primary text, headline, and description from the Asset Overlay</>,
      <>Set up the ad in the relevant platform (Google or Microsoft)</>,
      <>Validate the ad inside the platform</>,
    ],
    note: null,
  },
  {
    step: 3,
    title: 'Add the Preview Link',
    items: [
      <>Generate a Preview Link inside the advertising platform</>,
      <>Paste it into the <strong className="text-slate-800">Preview Link</strong> field in Content Tracker</>,
      <>You can also add multiple Preview Links if needed</>,
      <>This automatically notifies the Client/Approver and moves the row to <strong className="text-slate-800">Waiting for Preview Approval</strong></>,
    ],
    note: null,
  },
  {
    step: 4,
    title: 'Respond to Client Feedback (if any)',
    items: [
      <>If the client leaves feedback, you will receive a Teams notification</>,
      <>Review the feedback in the Preview Comments section of the Status Overlay</>,
      <>Update the platform setup and generate a new Preview Link if required</>,
      <>You can also add multiple Preview Links if needed</>,
    ],
    note: null,
  },
  {
    step: 5,
    title: 'Publish the Campaign',
    items: [
      <>Once the client has approved, publish the campaign in the advertising platform</>,
      <>Return to Content Tracker and set <strong className="text-slate-800">Live Column = YES</strong></>,
      <>This generates the Live Date and sends a Teams notification: "Campaign is now live"</>,
    ],
    note: null,
  },
]

const SOCIAL_LIVE_STEPS = [
  {
    step: 1,
    title: 'Navigate to the Campaign',
    items: [
      <>When informed of an update to a live campaign, navigate to the campaign in Content Tracker (referencing the Campaign ID)</>,
      <>Open the <strong className="text-slate-800">Asset Modal</strong></>,
    ],
  },
  {
    step: 2,
    title: 'Review Update Brief',
    items: [
      <>Scroll down to the <strong className="text-slate-800">Asset Feedback</strong> section</>,
      <>Here, all the information needed for updating the live campaign will be specified</>,
    ],
  },
  {
    step: 3,
    title: 'Implement Updates',
    items: [
      <>Update the <strong className="text-slate-800">live status to No</strong></>,
      <>Implement updates to the campaign in the platform and update the campaign accordingly</>,
      <>Once the campaign is updated in accordance with the brief, generate a new platform preview link and insert it in Content Tracker</>,
    ],
  },
  {
    step: 4,
    title: 'Await Approval & Go Live',
    items: [
      <>The client will now be notified</>,
      <>Once the preview link has been approved, change the live status back to <strong className="text-slate-800">Live = Yes</strong></>,
      <>The Go-live date is now saved as today's date (Go-live date can also be manually updated)</>,
    ],
  },
]

const SEARCH_LIVE_STEPS = [
  {
    step: 1,
    title: 'Navigate to the Campaign',
    items: [
      <>When informed of an update to a live campaign, navigate to the campaign in Content Tracker (referencing the Campaign ID)</>,
      <>Open the Asset Modal</>,
      <>A new version of the existing live campaign has been created by the Creative Agency</>,
    ],
  },
  {
    step: 2,
    title: 'Review Update Brief',
    items: [
      <>Scroll down to the <strong className="text-slate-800">Asset Feedback</strong> section</>,
      <>Here, all the information needed for updating the live campaign will be specified</>,
      <>Use the new version of the campaign as a reference</>,
    ],
  },
  {
    step: 3,
    title: 'Implement Updates',
    items: [
      <>Update the live status to <strong className="text-slate-800">No</strong></>,
      <>Implement updates to the campaign in the platform and update the campaign accordingly</>,
      <>Once the campaign is updated in accordance with the brief, generate a new platform preview link and insert it in Content Tracker</>,
    ],
  },
  {
    step: 4,
    title: 'Await Approval & Go Live',
    items: [
      <>The client will now be notified</>,
      <>Once the preview link has been approved, change the live status back to <strong className="text-slate-800">Live = Yes</strong></>,
      <>The Go-live date is now saved as today's date (Go-live date can also be manually updated)</>,
    ],
  },
]

function StepList({ steps, color }: { steps: { step: number; title: string; items: React.ReactNode[]; note?: string | null }[]; color: string }) {
  return (
    <div className="space-y-0">
      {steps.map(({ step, title, items, note }, i, arr) => (
        <div key={step} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 rounded-full bg-${color}-100 text-${color}-700 text-xs font-bold flex items-center justify-center shrink-0 z-10`}>
              {step}
            </div>
            {i < arr.length - 1 && (
              <div className="w-px flex-1 bg-slate-200 my-1.5" />
            )}
          </div>
          <div className={`min-w-0 flex-1 ${i < arr.length - 1 ? 'pb-6' : ''}`}>
            <p className="text-sm font-semibold text-slate-800 mt-0.5 mb-2">{title}</p>
            <ul className="space-y-1.5">
              {items.map((item, j) => (
                <li key={j} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                  <span className={`text-${color}-300 shrink-0 mt-0.5`}>•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {note && (
              <div className={`mt-3 border-l-[3px] border-${color}-300 pl-3 py-0.5`}>
                <p className={`text-sm text-${color}-800 leading-relaxed`}>{note}</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const VIDEOS: VideoGuide[] = [
  { title: 'Activation Process', description: 'End-to-end activation walkthrough', src: '/api/static/content-tracker/about-videos/Activation process.mp4', channel: 'both' },
]

export function ActivationTab() {
  const [channel, setChannel] = useState<Channel>('social');

  return (
    <div className="flex gap-8 max-w-6xl mx-auto">
      <div className="flex-1 min-w-0 space-y-6">
        <ChannelToggle value={channel} onChange={setChannel} color="green" />

        <Card className="shadow-none border-slate-300">
          <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Activation</p>
          </div>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                <PlayCircle className="w-5 h-5 text-green-700" strokeWidth={1.75} />
              </div>
              <CardTitle className="text-xl">Activation Team Guidelines</CardTitle>
            </div>
            <CardDescription className="leading-relaxed mt-1">
              Set up approved assets in ad platforms and manage the journey from preview to live.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-l-[3px] border-green-400 pl-4 py-0.5">
              <p className="text-sm text-slate-700 leading-relaxed">
                Your job is to take approved creative assets, configure them correctly in the advertising platforms, obtain client approval, and publish campaigns.
              </p>
            </div>
            <div className="border-l-[3px] border-green-300 pl-4 py-0.5">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-green-700 mb-1">Starts when</p>
              <p className="text-sm text-slate-700 leading-relaxed">
                You are notified via Teams when rows are ready. Look for rows with status: <strong className="text-slate-800">Activation in Setup</strong>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Process */}
        <Card className="shadow-none border-slate-300">
          <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Process</p>
          </div>
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">Your 5-step process</CardTitle>
          </CardHeader>
          <CardContent>
            <StepList steps={channel === 'social' ? SOCIAL_STEPS : SEARCH_STEPS} color="green" />
          </CardContent>
        </Card>

        {/* Live Updates */}
        <Card className="shadow-none border-slate-300">
          <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Live updates</p>
          </div>
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">Making updates to live campaigns</CardTitle>
            <CardDescription className="leading-relaxed mt-1">
              When clients request an update to live campaigns, they will inform the Creative Agency & the Planning team. The planning team will notify the Activation team on Wrike, referencing the Campaign ID. You will also receive a notification in the market-specific Teams channel.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <StepList steps={channel === 'social' ? SOCIAL_LIVE_STEPS : SEARCH_LIVE_STEPS} color="green" />
          </CardContent>
        </Card>
      </div>

      <div className="w-64 flex-shrink-0 sticky top-20 self-start">
        <VideoGuidePanel videos={VIDEOS} />
      </div>
    </div>
  )
}
